/** Custom presentation for Kaching offers. Kaching keeps ownership of discount
 * configuration and generates the cart items, including upsell/bundle metadata.
 */
(() => {
  if (customElements.get('ext-custom-bundle')) return;

  class ExtCustomBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.bindBuyButtons(); this.bindKachingEvents(); this.loadKachingDeals(); this.refresh(); this.resizeObserver?.observe(this);
        this.formObserver?.observe(this.container, {childList: true, subtree: true, attributes: true, attributeFilter: ['deal-block', 'product', 'config']});
        return;
      }
      this.initialized = true;
      this.items = new Map();
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currencyDisplay: 'narrowSymbol', currency: this.dataset.currency || 'AUD'
      });
      this.querySelectorAll('[data-bundle-item]').forEach(item => this.initializeItem(item));
      this.addEventListener('click', event => this.handleSwatchClick(event));
      this.addEventListener('change', event => this.handleChange(event));
      this.handleBuyButton = this.handleBuyButton.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.bindBuyButtons();
      this.bindKachingEvents();
      this.loadKachingDeals();
      this.resizeObserver = new ResizeObserver(() => {
        this.alignRadios();
        if (this.isVisible()) this.refresh();
        else this.restoreBuyButtons();
      });
      this.resizeObserver.observe(this);
      this.updateSupport();
      this.refresh();
      this.loadProductData();
      // A Theme Editor insertion may upgrade this element before the Buy buttons markup exists.
      this.formObserver = new MutationObserver(() => {
        if (!this.form?.isConnected) {
          const previousForm = this.form;
          this.bindBuyButtons();
          if (this.form && this.form !== previousForm) this.refresh();
        }
        const widget = this.getKachingWidget();
        const signature = widget ? ['deal-block', 'product', 'config'].map(name => widget.getAttribute(name)).join('|') : null;
        if (signature !== this.kachingSignature) {
          this.kachingSignature = signature;
          this.nativePricing = null;
          this.bindKachingEvents();
          this.loadKachingDeals();
          this.refresh();
        }
      });
      this.formObserver.observe(this.container, {childList: true, subtree: true, attributes: true, attributeFilter: ['deal-block', 'product', 'config']});
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect(); clearTimeout(this.galleryTimer);
      this.formObserver?.disconnect();
      clearTimeout(this.kachingApiTimer);
      this.kachingEventAbort?.abort();
      this.nativePricing = null;
      this.container?.removeEventListener('click', this.handleBuyButton, true);
      this.container?.removeEventListener('submit', this.handleFormSubmit, true);
      if (this.afterButtons) {
        this.append(...this.afterButtons.children);
        this.afterButtons.remove();
      }
    }

    bindBuyButtons() {
      this.container = this.closest('.shopify-section') || this.parentElement;
      const previousForm = this.form;
      this.form = [...this.container.querySelectorAll('product-form form')].find(form => form.querySelector('[name="id"]'));
      if (previousForm && previousForm !== this.form) this.restoreBuyButtons();
      if (!this.form) return;
      this.buyButton = this.form.querySelector('[type="submit"]');
      this.container.addEventListener('click', this.handleBuyButton, true);
      this.container.addEventListener('submit', this.handleFormSubmit, true);
      this.moveReassurance();
    }

    moveReassurance() {
      if (this.afterButtons?.isConnected) return;
      const affirmation = this.querySelector('.ext-custom-bundle__affirmation');
      if (!affirmation) return;
      this.afterButtons = document.createElement('div');
      this.afterButtons.className = 'ext-custom-bundle ext-custom-bundle__after-buttons';
      this.afterButtons.setAttribute('style', this.getAttribute('style'));
      if (affirmation) this.afterButtons.append(affirmation);
      this.form.closest('product-form').after(this.afterButtons);
    }

    handleBuyButton(event) {
      if (!this.isVisible()) return;
      const button = event.target.closest('button[type="submit"]');
      if (!button || button.form !== this.form) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (!button.disabled) this.addToCart();
    }

    handleFormSubmit(event) {
      if (!this.isVisible()) return;
      if (event.target !== this.form) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.addToCart();
    }

    getColourPosition(product) {
      return product.options.findIndex(option => /^(color|colour)$/i.test(typeof option === 'string' ? option : option.name));
    }

    updateSupport() {
      const first = this.items.values().next().value;
      const position = first ? this.getColourPosition(first.product) : -1;
      const count = position < 0 ? 0 : new Set(first.product.variants.filter(variant => variant.available).map(variant => variant.options[position])).size;
      this.querySelector('[data-support]').textContent = this.dataset.supporting.replaceAll('[count]', count);
    }

    async loadProductData() {
      // Refresh prices/availability from Shopify's storefront endpoint. The dev
      // renderer can serialize available inventory as false in Liquid product JSON.
      const handles = [...new Set([...this.items.values()].map(item => item.product.handle).filter(Boolean))];
      this.loadingProducts = true;
      this.refresh();
      await Promise.all(handles.map(async handle => {
        try {
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(handle) + '.js');
          if (!response.ok) return;
          const product = await response.json();
          if (!Array.isArray(product.variants)) return;
          this.items.forEach(item => {
            if (item.product.handle !== handle) return;
            const selectedId = item.variant?.id;
            item.product = product;
            item.variant = product.variants.find(variant => variant.id === selectedId);
            this.refreshItem(item);
          });
        } catch (_) { /* Keep server-rendered product data when the endpoint is unavailable. */ }
      }));
      this.loadingProducts = false;
      if (!this.isConnected) return;
      this.updateSupport();
      this.refresh();
    }

    initializeItem(element) {
      const product = JSON.parse(element.querySelector('[data-product]').textContent);
      const selected = product.variants.find(variant => String(variant.id) === element.dataset.selectedVariant) || product.variants[0];
      if (!selected) return;
      const mediaMap = JSON.parse(element.querySelector('[data-media-map]').textContent);
      const item = { element, product, mediaMap, values: [...selected.options], variant: selected };
      this.items.set(element, item);
      const controls = element.querySelector('[data-options]');
      if (product.variants.length === 1 && product.variants[0].title === 'Default Title') return;
      product.options.forEach((option, position) => {
        const name = typeof option === 'string' ? option : option.name;
        const isColour = position === this.getColourPosition(product);
        const labelTemplate = isColour ? this.dataset.colourLabel : /^size$/i.test(name) ? this.dataset.sizeLabel : name;
        const label = labelTemplate.replaceAll('[index]', element.dataset.itemNumber);
        const values = [...new Set(product.variants.map(variant => variant.options[position]))];
        const group = document.createElement('fieldset');
        group.className = 'ext-custom-bundle__option';
        const legend = document.createElement('legend');
        if (isColour) {
          const labelText = document.createElement('span');
          labelText.dataset.colourLabel = '';
          labelText.textContent = label + ': ';
          const selectedColour = document.createElement('strong');
          selectedColour.dataset.selectedColour = '';
          legend.append(labelText, selectedColour);
        } else legend.textContent = label;
        group.classList.toggle('ext-custom-bundle__option--colour', isColour);
        group.append(legend);
        if (isColour) {
          const swatches = document.createElement('div');
          swatches.className = 'ext-custom-bundle__swatches';
          values.forEach(value => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ext-custom-bundle__swatch';
            button.dataset.optionPosition = position;
            button.dataset.optionValue = value;
            button.setAttribute('aria-label', label + ': ' + value);
            const imageVariant = product.variants.find(variant => variant.options[position] === value && variant.featured_image);
            const imageSource = imageVariant?.featured_image?.src || product.featured_image;
            if (imageSource) {
              const image = document.createElement('img');
              image.src = imageSource;
              image.alt = '';
              image.width = 100;
              image.height = 100;
              image.loading = 'lazy';
              button.append(image);
            }
            const caption = document.createElement('span');
            caption.className = 'visually-hidden';
            caption.textContent = value;
            button.append(caption);
            swatches.append(button);
          });
          group.append(swatches);
        } else {
          const select = document.createElement('select');
          select.dataset.optionPosition = position;
          select.setAttribute('aria-label', label);
          values.forEach(value => {
            const optionElement = document.createElement('option');
            optionElement.value = value;
            optionElement.textContent = value;
            select.append(optionElement);
          });
          group.append(select);
        }
        controls.append(group);
      });
      this.refreshItem(item);
    }

    handleSwatchClick(event) {
      if (event.target.matches('[data-tier]')) {
        this.defaultAddonOffer = null;
        this.refresh();
        return;
      }
      const button = event.target.closest('button[data-option-value]');
      if (!button || button.disabled) return;
      this.selectOption(button, button.dataset.optionValue);
    }

    handleChange(event) {
      if (event.target.matches('select[data-option-position]')) this.selectOption(event.target, event.target.value);
      else {
        const offer = event.target.closest('[data-offer]');
        if (event.target.matches('[data-tier]')) this.defaultAddonOffer = null;
        if (event.target.matches('[data-addon-toggle]')) {
          offer.querySelector('[data-tier]').checked = true;
          // An explicit checkbox choice takes priority over the default setting.
          this.lastSelectedOffer = offer;
          this.defaultAddonOffer = offer;
        }
        this.refresh();
      }
    }

    selectOption(control, value) {
      const item = this.items.get(control.closest('[data-bundle-item]'));
      const position = Number(control.dataset.optionPosition);
      const previousValue = item.values[position];
      const offer = item.element.closest('[data-offer]');
      const offerItems = [...(offer?.querySelectorAll('[data-items] [data-bundle-item]') || [])].map(element => this.items.get(element));
      let syncDisabled = false;
      try { syncDisabled = Boolean(JSON.parse(this.getKachingWidget()?.getAttribute('deal-block') || '{}').disableVariantOptionSync); }
      catch (_) { syncDisabled = true; }
      // Kaching links only Item 1, and only while every other item still shares
      // the changed option's old value. Other options (such as size) stay intact.
      const followers = offerItems.slice(1);
      const syncFollowers = !syncDisabled && offerItems[0] === item && followers.length > 0
        && offerItems.every(candidate => candidate && String(candidate.product.id) === String(item.product.id))
        && followers.every(candidate => candidate.values[position] === previousValue);
      item.values[position] = value;
      item.variant = item.product.variants.find(variant => variant.options.every((option, index) => option === item.values[index]));
      this.refreshItem(item);
      if (syncFollowers) followers.forEach(candidate => {
        const values = [...candidate.values];
        values[position] = value;
        const variant = candidate.product.variants.find(variant => variant.options.every((option, index) => option === values[index]));
        if (!variant?.available) return;
        candidate.values = values;
        candidate.variant = variant;
        this.refreshItem(candidate);
      });
      this.refresh();
      this.scheduleGallerySync(item);
    }

    refreshItem(item) {
      const selectedColour = item.element.querySelector('[data-selected-colour]');
      if (selectedColour) selectedColour.textContent = item.values[this.getColourPosition(item.product)] || '';
      item.element.querySelectorAll('[data-option-position]').forEach(control => {
        const position = Number(control.dataset.optionPosition);
        if (control.tagName === 'SELECT') control.value = item.values[position];
        else control.setAttribute('aria-pressed', String(control.dataset.optionValue === item.values[position]));
        const choices = control.tagName === 'SELECT' ? [...control.options] : [control];
        choices.forEach(choice => {
          const value = choice.value || choice.dataset.optionValue;
          choice.disabled = !item.product.variants.some(variant => variant.available && variant.options.every((option, index) => option === (index === position ? value : item.values[index])));
        });
      });
      item.element.querySelector('[data-item-price]').textContent = item.variant ? this.formatMoney(item.variant.price) : '—';
      const compare = item.element.querySelector('[data-item-compare]');
      compare.textContent = this.formatMoney(item.variant?.compare_at_price || 0);
      compare.hidden = !item.variant || item.variant.compare_at_price <= item.variant.price;
      const image = item.element.querySelector('.ext-custom-bundle__product-image');
      if (image && item.variant?.featured_image?.src) {
        image.removeAttribute('srcset');
        image.src = item.variant.featured_image.src;
      }
    }

    formatMoney(cents) { return this.money.format(cents / 100); }

    getKachingWidget() {
      return [...(this.container?.querySelectorAll('kaching-bundles-block') || [])].find(widget => {
        try { return String(JSON.parse(widget.getAttribute('product')).id) === this.dataset.productId; }
        catch (_) { return false; }
      });
    }

    bindKachingEvents() {
      this.kachingEventAbort?.abort();
      this.kachingEventAbort = new AbortController();
      const options = {capture: true, signal: this.kachingEventAbort.signal};
      // The loaded event does not bubble; capture also handles newly inserted widgets.
      this.container.addEventListener('kaching-bundles-block-loaded', event => {
        if (event.target !== this.getKachingWidget()) return;
        this.nativePricing = null;
        this.loadKachingDeals();
        this.refresh();
      }, options);
      this.container.addEventListener('items-changed', event => {
        if (event.target !== this.getKachingWidget() || !this.isVisible() || this.syncingKaching || this.submitting) return;
        const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
        const context = offer && this.getKachingOffer(offer);
        // Late native events can report a stale offer; only accept verified items.
        try {
          this.nativePricing = context && context.widget.selectedDealBarId === context.bar.id &&
            this.matchesKachingSelection(context.widget.items(), this.getSelectedItems())
            ? this.readKachingPricing(this.getSelectionKey(), context.widget) : null;
        } catch (_) { this.nativePricing = null; }
        this.failedPricingKey = null;
        this.refresh();
      }, options);
    }

    async loadKachingDeals() {
      if (!this.isConnected || this.fetchingKachingDeals) return;
      const api = window.kachingBundlesApi;
      if (typeof api?.fetchDeals !== 'function') {
        if ((this.kachingApiAttempts = (this.kachingApiAttempts || 0) + 1) <= 40) {
          clearTimeout(this.kachingApiTimer);
          this.kachingApiTimer = setTimeout(() => this.loadKachingDeals(), 250);
        } else if (this.isVisible()) this.setCartError('Kaching offers could not be loaded. Please refresh and try again.');
        return;
      }
      this.fetchingKachingDeals = true;
      try {
        const deals = await api.fetchDeals();
        if (!Array.isArray(deals)) throw new Error('Invalid Kaching deal response');
        this.kachingDeals = deals;
        this.nativePricing = null;
        this.failedPricingKey = null;
      } catch (_) {
        this.kachingDeals = null; // Never submit using stale deal data after a failed reload.
        if (this.isConnected && this.isVisible()) this.setCartError('Kaching offers could not be loaded. Please refresh and try again.');
      } finally {
        this.fetchingKachingDeals = false;
        if (this.isConnected) this.refresh();
      }
    }

    getSelectionKey() {
      const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      const context = offer && this.getKachingOffer(offer);
      return JSON.stringify([context?.deal.id, context?.bar.id, this.isAddonSelected(),
        this.getSelectedItems().map(item => [item.product.id, item.variant?.id, item.values])]);
    }

    matchesKachingSelection(lines, selected) {
      const expected = new Map();
      selected.forEach(item => expected.set(Number(item.variant.id), (expected.get(Number(item.variant.id)) || 0) + 1));
      lines.forEach(line => expected.set(Number(line.id), (expected.get(Number(line.id)) || 0) - Number(line.quantity)));
      return lines.length > 0 && [...expected.values()].every(quantity => quantity === 0);
    }

    readKachingPricing(key, widget) {
      const pricing = widget.pricing();
      // Kaching's public pricing().amount is in currency units, not cents.
      const price = pricing?.discountedPrice?.amount;
      const compare = pricing?.fullPrice?.amount;
      if (typeof price !== 'number' || typeof compare !== 'number' || !Number.isFinite(price) || !Number.isFinite(compare) || price < 0 || compare < 0) throw new Error('Kaching pricing is not ready.');
      return {key, price: Math.round(price * 100), compare: Math.round(compare * 100)};
    }

    syncKachingPricing() {
      if (!this.isVisible() || !this.form || this.loadingProducts || this.submitting || this.syncingKaching) return;
      const key = this.getSelectionKey();
      if (this.nativePricing?.key === key || this.failedPricingKey === key) return;
      const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      const context = offer && this.getKachingOffer(offer);
      if (!context || typeof context.widget.pricing !== 'function') return;
      this.syncingKaching = true;
      this.getKachingCartItems({rotateNonce: false, selectionKey: key}).then(() => {
        if (!this.isConnected || !this.isVisible() || this.getSelectionKey() !== key) return;
        this.nativePricing = this.readKachingPricing(key, context.widget);
        this.failedPricingKey = null;
        this.setCartError('');
      }).catch(error => {
        if (this.isConnected && this.isVisible() && this.getSelectionKey() === key) {
          this.failedPricingKey = key;
          this.setCartError(error.message);
        }
      }).finally(() => {
        this.syncingKaching = false;
        if (this.isConnected) this.refresh();
      });
    }

    getKachingOffer(offer) {
      const widget = this.getKachingWidget();
      if (!widget) return null;
      try {
        const dealId = widget.getAttribute('deal-block-id') || JSON.parse(widget.getAttribute('deal-block')).id;
        const apiDeal = this.kachingDeals?.find(deal => String(deal.id) === String(dealId));
        if (!apiDeal) return null;
        // Normalize the public API's productId/discount fields for tier previews.
        const normalize = entry => ({...entry, productGID: entry.productId,
          discountType: entry.discount?.type, discountValue: entry.discount?.value});
        const deal = {...apiDeal, dealBars: apiDeal.dealBars.map(bar => ({...normalize(bar),
          bundleProducts: bar.bundleProducts?.map(normalize), upsells: (bar.upsells || []).map(normalize)}))};
        const config = JSON.parse(widget.getAttribute('config')) || {};
        const elements = [...offer.querySelectorAll('[data-bundle-item]')].filter(element => !element.closest('.ext-custom-bundle__addon'));
        const products = elements.map(element => this.items.get(element));
        const matches = deal.dealBars.filter(bar => {
          if (bar.showAsSoldOutEnabled) return false;
          if (!offer.classList.contains('ext-custom-bundle__offer--complete')) {
            return (!bar.dealBarType || bar.dealBarType === 'quantity-break') && Number(bar.quantity) === products.length;
          }
          if (bar.dealBarType !== 'bundle') return false;
          const expected = new Map();
          (bar.bundleProducts || []).forEach(product => {
            const id = product.productGID === 'default' ? this.dataset.productId : String(product.productGID).split('/').pop();
            expected.set(id, (expected.get(id) || 0) + Number(product.quantity));
          });
          products.forEach(item => expected.set(String(item.product.id), (expected.get(String(item.product.id)) || 0) - 1));
          return [...expected.values()].every(quantity => quantity === 0);
        });
        // Ambiguous or unsupported offers must never silently use a different deal.
        if (matches.length !== 1) return null;
        const bar = matches[0];
        const addon = this.items.get(offer.querySelector('[data-addon-items] [data-bundle-item]'));
        const upsells = (bar.upsells || []).filter(upsell => String(upsell.productGID).split('/').pop() === String(addon?.product.id) && Number(upsell.quantity) === 1);
        return {widget, deal, bar, config, upsell: upsells.length === 1 ? upsells[0] : null};
      } catch (_) { return null; }
    }

    applyKachingPrice(price, rule, context, quantity = 1) {
      const amount = Number(rule.discountValue);
      const rate = Number(context.config.currencyRate || 1);
      if (rule.discountType === 'specific') return Math.max(0, Math.min(price, Math.round(amount * rate * 100)));
      if (rule.discountType === 'percentage') return Math.max(0, Math.min(price, Math.ceil(price * (100 - amount) / 100)));
      if (rule.discountType === 'amount') return Math.max(0, price - Math.round(amount * 100) * rate * quantity);
      return price;
    }

    async syncKachingOptions(row, item) {
      if (!row && item.product.variants?.length === 1) return;
      if (!row) throw new Error('Kaching could not match this product selection. Please refresh and try again.');
      for (const [position, option] of item.product.options.entries()) {
        const name = typeof option === 'string' ? option : option.name;
        const control = [...row.querySelectorAll('[data-name]')].find(element => element.getAttribute('data-name') === name);
        if (!control) continue; // Single-value options may have no rendered control.
        const value = item.values[position];
        if (control.tagName === 'SELECT') {
          if (control.value === value) continue;
          control.value = value;
          control.dispatchEvent(new Event('change', {bubbles: true}));
        } else {
          if (control.getAttribute('data-value') === value) continue;
          const choice = [...control.querySelectorAll('[data-value]')].find(element => element.getAttribute('data-value') === value);
          if (!choice) throw new Error('This option is not available in the Kaching offer.');
          choice.click();
        }
        // The widget and its native-form observers both update asynchronously.
        await new Promise(resolve => setTimeout(resolve, 25));
      }
    }

    async getKachingCartItems({rotateNonce = true, selectionKey} = {}) {
      const assertCurrentSelection = () => {
        if (selectionKey !== undefined && (!this.isConnected || !this.isVisible() || this.getSelectionKey() !== selectionKey)) throw new Error('The bundle selection changed.');
      };
      assertCurrentSelection();
      const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      const context = offer && this.getKachingOffer(offer);
      if (!context || typeof context.widget.items !== 'function') throw new Error('The Kaching offer is still loading or does not match this bundle. Please refresh and try again.');
      const {widget, bar} = context;
      if (rotateNonce) widget.rotateBundleNonce?.();
      widget.selectedDealBarId = bar.id;
      await new Promise(resolve => setTimeout(resolve, 0));
      assertCurrentSelection();
      const nativeBar = [...widget.querySelectorAll('[data-deal-bar-id]')].find(element => element.getAttribute('data-deal-bar-id') === bar.id);
      if (!nativeBar) throw new Error('The Kaching offer is not ready. Please refresh and try again.');
      const selected = this.getSelectedItems();
      const mainItems = selected.filter(item => !item.element.closest('.ext-custom-bundle__addon'));
      const syncMainItems = async () => {
        for (const [index, item] of mainItems.entries()) {
          assertCurrentSelection();
          // Native variant changes can rebuild/reset sibling option rows.
          const rows = [...nativeBar.querySelectorAll('.kaching-bundles__bar-variant')].filter(row => !row.closest('.kaching-bundles__upsell'));
          await this.syncKachingOptions(rows[index], item);
        }
      };
      await syncMainItems();
      for (const upsell of bar.upsells || []) {
        assertCurrentSelection();
        const nativeUpsell = [...nativeBar.querySelectorAll('[data-upsell-id]')].find(element => element.getAttribute('data-upsell-id') === upsell.id);
        const toggle = nativeUpsell?.querySelector('.kaching-bundles__upsell__checkbox');
        const wanted = this.isAddonSelected() && context.upsell?.id === upsell.id;
        if (!toggle && wanted) throw new Error('The Kaching add-on is not ready. Please refresh and try again.');
        if (toggle && toggle.classList.contains('kaching-bundles__upsell__checkbox--selected') !== wanted) {
          toggle.click();
          await new Promise(resolve => setTimeout(resolve, 0));
        }
        if (wanted) {
          const item = selected.find(item => item.element.closest('.ext-custom-bundle__addon'));
          const row = nativeUpsell.querySelector('.kaching-bundles__bar-variant');
          if (row) await this.syncKachingOptions(row, item);
        }
      }
      // Verify Kaching selected exactly our variants and quantities before sending.
      const matchesSelection = lines => this.matchesKachingSelection(lines, selected);
      let lines;
      for (let attempt = 0; attempt < 4; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 25));
        assertCurrentSelection();
        lines = widget.items();
        if (matchesSelection(lines)) {
          // Require the selection to survive a further widget/form update.
          await new Promise(resolve => setTimeout(resolve, 25));
          assertCurrentSelection();
          lines = widget.items();
          if (matchesSelection(lines)) break;
        }
        if (attempt < 3) await syncMainItems();
      }
      if (!matchesSelection(lines)) throw new Error('The bundle selection could not be matched to Kaching. Please refresh and try again.');
      const validation = widget.validateItemSelection?.();
      if (validation && !validation.valid) throw new Error(validation.message || 'Please complete the Kaching product options.');
      const properties = {};
      new FormData(this.form).forEach((value, name) => {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && match[1] !== '__kaching_bundles') properties[match[1]] = value;
      });
      const mainIds = new Set(mainItems.filter(item => String(item.product.id) === this.dataset.productId).map(item => Number(item.variant.id)));
      return lines.map(line => ({...line, properties: {...(mainIds.has(Number(line.id)) ? properties : {}), ...line.properties}}));
    }

    getAddonTotals(offer) {
      const element = offer.querySelector('[data-addon-items] [data-bundle-item]');
      const variant = this.items.get(element)?.variant;
      const context = this.getKachingOffer(offer);
      const percent = context?.upsell?.discountType === 'percentage' ? Number(context.upsell.discountValue) : 0;
      const original = variant?.price || 0;
      const price = context?.upsell ? this.applyKachingPrice(original, context.upsell, context) : original;
      return {price, compare: price < original ? original : (variant?.compare_at_price || 0), percent};
    }

    getOfferTotals(offer) {
      const context = this.getKachingOffer(offer);
      const elements = [...offer.querySelectorAll('[data-bundle-item]')].filter(element => !element.closest('.ext-custom-bundle__addon'));
      const total = elements.reduce((total, element) => {
        const variant = this.items.get(element)?.variant;
        const item = this.items.get(element);
        const rule = context?.bar.dealBarType === 'bundle' ? context.bar.bundleProducts.find(product => (product.productGID === 'default' ? this.dataset.productId : String(product.productGID).split('/').pop()) === String(item?.product.id)) : null;
        total.price += rule ? this.applyKachingPrice(variant?.price || 0, rule, context) : variant?.price || 0;
        total.compare += Math.max(variant?.compare_at_price || 0, variant?.price || 0);
        return total;
      }, {price: 0, compare: 0});
      if (context && context.bar.dealBarType !== 'bundle') {
        if (context.bar.discountType === 'percentage' && !context.config.featureFlags?.percentage_cents_rounding_workaround) {
          total.price = elements.reduce((price, element) => price + this.applyKachingPrice(this.items.get(element)?.variant?.price || 0, context.bar, context), 0);
        } else total.price = this.applyKachingPrice(total.price, context.bar, context, elements.length);
      }
      return total;
    }

    updateSavingsSubtitle(offer, total) {
      const subtitle = offer.querySelector('[data-auto-savings-subtitle]');
      if (!subtitle) return;
      const context = this.getKachingOffer(offer);
      let percentage = 0;
      if (context && !this.loadingProducts && Number.isFinite(total.price) && Number.isFinite(total.compare) && total.price >= 0) {
        let compare = total.compare;
        try {
          const mountedDeal = JSON.parse(context.widget.getAttribute('deal-block'));
          if (mountedDeal.useProductCompareAtPrice === false) {
            compare = [...offer.querySelectorAll('[data-bundle-item]')]
              .filter(element => !element.closest('.ext-custom-bundle__addon'))
              .reduce((sum, element) => sum + (this.items.get(element)?.variant?.price || 0), 0);
          }
          if (compare > total.price && compare > 0) {
            percentage = Math.round(Math.round((compare - total.price) / compare * 10000) / 100);
            if (context.config.featureFlags?.legacy_saved_percentage && context.bar.dealBarType !== 'bundle' && context.bar.discountType === 'percentage') {
              percentage = Math.round(Number(context.bar.discountValue));
            }
          }
        } catch (_) { /* An unreadable native deal must not advertise savings. */ }
      }
      const show = Number.isFinite(percentage) && percentage > 0 && percentage <= 100;
      subtitle.textContent = show ? 'You save ' + percentage + '%' : '';
      subtitle.hidden = !show;
    }

    refresh() {
      this.querySelector('.ext-custom-bundle__offers').disabled = Boolean(this.submitting || this.loadingProducts);
      const offers = [...this.querySelectorAll('[data-offer]')];
      const currentOffer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      if (currentOffer !== this.lastSelectedOffer) {
        this.lastSelectedOffer = currentOffer;
        this.defaultAddonOffer = null;
      }
      let selectedOffer;
      offers.forEach(offer => {
        const selected = offer.querySelector('[data-tier]').checked;
        offer.classList.toggle('is-selected', selected);
        offer.querySelector('[data-items]').hidden = !selected;
        offer.querySelectorAll('[data-options]').forEach(options => {
          if (!options.closest('.ext-custom-bundle__addon')) options.hidden = !selected;
        });
        const total = this.getOfferTotals(offer);
        this.updateSavingsSubtitle(offer, total);
        offer.querySelector('[data-price]').textContent = this.formatMoney(total.price);
        const compare = offer.querySelector('[data-compare]');
        compare.textContent = this.formatMoney(total.compare);
        compare.hidden = total.compare <= total.price;
        const saving = offer.querySelector('[data-saving]');
        if (saving) {
          const template = saving.dataset.badgeTemplate;
          saving.textContent = template.replaceAll('[saving]', this.formatMoney(total.compare - total.price));
          saving.hidden = template.includes('[saving]') && total.compare <= total.price;
        }
        const toggle = offer.querySelector('[data-addon-toggle]');
        if (toggle) {
          const context = this.getKachingOffer(offer);
          toggle.closest('.ext-custom-bundle__addon').hidden = !context?.upsell;
          if (selected && context?.upsell && this.defaultAddonOffer !== offer) {
            toggle.checked = this.dataset.addonDefault === 'true';
            this.defaultAddonOffer = offer;
          }
          if (!context?.upsell) toggle.checked = false;
          if (!selected) toggle.checked = false;
          const addon = offer.querySelector('[data-addon-items]');
          addon.hidden = !selected || !toggle.checked;
          const variant = this.items.get(addon.querySelector('[data-bundle-item]'))?.variant;
          const totals = this.getAddonTotals(offer);
          const label = offer.querySelector('[data-addon-label]');
          label.dataset.originalLabel ||= label.textContent;
          label.textContent = label.dataset.originalLabel + (totals.percent ? ` at ${totals.percent}% OFF` : '');
          offer.querySelector('[data-addon-price]').textContent = this.formatMoney(totals.price);
          const compare = offer.querySelector('[data-addon-compare]');
          compare.textContent = this.formatMoney(totals.compare);
          compare.hidden = !variant || totals.compare <= totals.price;
        }
        if (selected) selectedOffer = offer;
      });
      if (!selectedOffer) return;
      const selectedTotal = this.getOfferTotals(selectedOffer).price;
      const highestTotal = Math.max(...offers.map(offer => this.getOfferTotals(offer).price));
      const highestOffer = offers.find(offer => this.getOfferTotals(offer).price === highestTotal);
      const highestSaving = this.getOfferTotals(highestOffer).compare - highestTotal;
      const savingLabel = this.formatMoney(highestSaving);
      const percentage = highestTotal ? Math.min(100, Math.round(selectedTotal / highestTotal * 100)) : 0;
      const progress = this.querySelector('[role="progressbar"]');
      if (progress) {
        progress.setAttribute('aria-valuenow', percentage);
        this.querySelector('[data-progress-fill]').style.width = percentage + '%';
        this.querySelector('[data-progress-value]').textContent = percentage === 100 ? 'Best value' : savingLabel + ' off';
        this.querySelector('[data-progress-copy]').textContent = (percentage === 100 ? this.dataset.unlocked : this.dataset.message).replaceAll('[saving]', savingLabel);
      }
      const addonPrice = this.getAddonTotals(selectedOffer).price;
      const key = this.getSelectionKey();
      const total = this.nativePricing?.key === key ? this.nativePricing.price : selectedTotal + (this.isAddonSelected() ? addonPrice : 0);
      const totalLabel = this.querySelector('[data-total]');
      if (totalLabel) totalLabel.textContent = this.formatMoney(total);
      this.updateBuyButtons(total);
      this.alignRadios();
      this.syncKachingPricing();
    }

    isAddonSelected() {
      const checkbox = this.querySelector('[data-tier]:checked')?.closest('[data-offer]').querySelector('[data-addon-toggle]');
      return Boolean(checkbox?.checked && !checkbox.closest('.ext-custom-bundle__addon').hidden);
    }

    getSelectedItems() {
      const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      if (!offer) return [];
      return [...offer.querySelectorAll('[data-bundle-item]')]
        .filter(element => !element.closest('.ext-custom-bundle__addon') || this.isAddonSelected())
        .map(element => this.items.get(element));
    }

    scheduleGallerySync(item) {
      if (!this.isVisible()) return;
      if (String(item.product.id) !== this.dataset.productId) return;
      const mediaId = item.mediaMap[item.variant?.id];
      if (!mediaId) return;
      clearTimeout(this.galleryTimer);
      this.galleryTimer = setTimeout(() => {
        if (!this.isVisible()) return;
        const container = this.closest('quick-add-modal') || this.closest('.shopify-section') || this.parentElement;
        const gallery = container.querySelector('media-gallery');
        const media = gallery && [...gallery.querySelectorAll('[data-media-id]')].find(element => element.dataset.mediaId.endsWith('-' + mediaId));
        if (!media || typeof gallery.setActiveMedia !== 'function') return;
        if (this.closest('quick-add-modal') && window.__quickAddPdpModalEnhancer) {
          window.__quickAddPdpModalEnhancer.showVariantMedia(gallery, media.dataset.mediaId);
          return;
        }
        gallery.setActiveMedia(media.dataset.mediaId, false);
        if (window.matchMedia('(max-width: 749px)').matches) {
          // Let the gallery finish its own deferred scroll before returning
          // PDP shoppers to the header, including the above-gallery summary.
          setTimeout(() => {
            const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
            const top = container.querySelector('[id^="MainProduct-"]') ? 0 :
              Math.max(0, gallery.getBoundingClientRect().top + window.scrollY - (header?.offsetHeight || 0));
            window.scrollTo({top, behavior: 'smooth'});
          }, 0);
        }
      }, 120);
    }

    setCartError(message) {
      const error = this.querySelector('[data-cart-error]');
      const wrapper = this.form?.closest('product-form').querySelector('.product-form__error-message-wrapper');
      if (wrapper) {
        wrapper.hidden = !message;
        wrapper.querySelector('.product-form__error-message').textContent = message || '';
      }
      error.textContent = wrapper ? '' : message || '';
      error.hidden = Boolean(wrapper) || !message;
    }

    isVisible() {
      return this.isConnected && getComputedStyle(this).display !== 'none';
    }

    restoreBuyButtons() {
      clearTimeout(this.galleryTimer);
      this.nativeBuyButtonState?.forEach(state => {
        // Kaching may already own this field. Restore only our last written value.
        if (state.element[state.property] === state.lastValue) {
          state.element[state.property] = state.originalValue;
          if (state.property === 'disabled') {
            state.element.setAttribute('aria-disabled', String(state.originalValue));
            state.element.removeAttribute('data-unavailable');
          }
        }
      });
      this.nativeBuyButtonState = null;
    }

    updateBuyButtons(total) {
      if (!this.isVisible()) { this.restoreBuyButtons(); return; }
      if (!this.form?.isConnected) this.bindBuyButtons();
      if (!this.form || !this.buyButton) return;
      if (!this.nativeBuyButtonState) {
        // Kaching observes the variant field and resets all bundle choices when
        // it changes. Let its widget own that field throughout synchronization.
        const fields = [];
        this.container.querySelectorAll('button[type="submit"]').forEach(button => {
          if (button.form !== this.form) return;
          fields.push([button, 'disabled']);
          fields.push([button, 'className']);
          fields.push([button.querySelector('.loading-overlay__spinner'), 'className']);
          fields.push([button.querySelector('.main-atc__label__text, .sticky-atc__label__text'), 'textContent']);
          button.querySelectorAll('.main-atc-price, [id^="sticky-atc-price-"]').forEach(price => fields.push([price, 'className']));
        });
        this.container.querySelectorAll('[id^="sticky-atc-separate-price-"]').forEach(price => fields.push([price, 'textContent']));
        this.nativeBuyButtonState = fields.filter(([element]) => element).map(([element, property]) => ({element, property, originalValue: element[property]}));
      }
      const offer = this.querySelector('[data-tier]:checked')?.closest('[data-offer]');
      const available = this.getSelectedItems().length > 0 && this.getSelectedItems().every(item => item.variant?.available);
      this.container.querySelectorAll('[id^="sticky-atc-separate-price-"]').forEach(price => { price.textContent = this.formatMoney(total); });
      const buttons = [...this.container.querySelectorAll('button[type="submit"]')].filter(button => button.form === this.form);
      buttons.forEach(button => {
        button.disabled = !available || !this.getKachingOffer(offer) || this.nativePricing?.key !== this.getSelectionKey() || Boolean(this.submitting || this.loadingProducts || this.syncingKaching);
        button.toggleAttribute('data-unavailable', !available);
        button.setAttribute('aria-disabled', String(button.disabled));
        button.classList.toggle('loading', Boolean(this.submitting));
        button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !this.submitting);
        let label = button.querySelector('.main-atc__label__text, .sticky-atc__label__text');
        if (!label && button.querySelector('.sticky-atc__label')) {
          label = document.createElement('span');
          label.className = 'sticky-atc__label__text';
          label.textContent = button.dataset.label || window.variantStrings?.addToCart || 'Add to cart';
          button.querySelector('.sticky-atc__label').replaceChildren(label);
        }
        if (label) {
          if (!label.dataset.bundleOriginalLabel) label.dataset.bundleOriginalLabel = button.dataset.customLabel || button.dataset.label || window.variantStrings?.addToCart || label.textContent.trim();
          label.textContent = available ? label.dataset.bundleOriginalLabel + ' – ' + this.formatMoney(total) : (window.variantStrings?.soldOut || 'Sold out');
        }
        button.querySelectorAll('.main-atc-price, [id^="sticky-atc-price-"]').forEach(price => price.classList.add('hidden'));
      });
      this.nativeBuyButtonState.forEach(state => { state.lastValue = state.element[state.property]; });
    }

    async addToCart() {
      if (!this.isVisible()) return;
      if (this.submitting || this.loadingProducts || this.syncingKaching) return;
      if (!this.form || !this.buyButton) { this.setCartError('Add a Buy buttons block to this product section.'); return; }
      if (this.nativePricing?.key !== this.getSelectionKey()) {
        this.setCartError('Please wait for Kaching to confirm this bundle and price.');
        this.syncKachingPricing();
        return;
      }
      if (Number(this.buyButton.dataset.requiredFields || 0) > Number(this.buyButton.dataset.validFields || 0)) {
        this.setCartError('Please complete the required product fields.'); return;
      }
      const selected = this.getSelectedItems();
      if (!selected.length || selected.some(item => !item?.variant?.available)) {
        this.setCartError('Please choose available options for every item.');
        return;
      }
      const cart = this.dataset.cartType === 'page' ? null : document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender().map(section => section.id);
      this.submitting = true;
      this.setAttribute('aria-busy', 'true');
      this.setCartError('');
      this.refresh();
      let added = false;
      try {
        const items = await this.getKachingCartItems();
        if (!this.isVisible()) return;
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items, ...(sections ? {sections, sections_url: window.location.pathname} : {})})
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add these items. Please try again.');
        added = true;
        if (this.form.querySelector('[name="id"]')?.dataset.skipCart === 'true') { window.location.assign(root + 'checkout'); return; }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'ext-custom-bundle'});
        if (!cart || !sections.every(id => data.sections?.[id])) {
          window.location.assign(this.dataset.cartUrl || root + 'cart');
          return;
        }
        cart.setActiveElement?.(this.buyButton);
        cart.classList.remove('is-empty');
        const state = {...data, id: data.items?.[0]?.id, key: data.items?.[0]?.key};
        const modal = this.closest('quick-add-modal');
        if (modal) {
          document.body.addEventListener('modalClosed', () => cart.renderContents(state), {once: true});
          modal.hide(true);
        } else cart.renderContents(state);
      } catch (error) {
        // The products may already be added if only drawer rendering failed.
        if (added) window.location.assign(this.dataset.cartUrl || '/cart');
        else this.setCartError(error.message || 'Unable to add these items. Please try again.');
      } finally {
        this.submitting = false;
        this.removeAttribute('aria-busy');
        this.refresh();
      }
    }

    // Match the compact layout's title/radio alignment, including its mobile correction.
    alignRadios() {
      this.querySelectorAll('[data-offer]').forEach(offer => {
        const radio = offer.querySelector('[data-tier]');
        const title = offer.querySelector('.ext-custom-bundle__tier-title');
        radio.style.marginTop = '0px';
        const correction = window.matchMedia('(max-width: 749px)').matches ? 2 : 0;
        radio.style.marginTop = Math.max(0, title.getBoundingClientRect().top - radio.getBoundingClientRect().top + correction) + 'px';
      });
    }
  }

  customElements.define('ext-custom-bundle', ExtCustomBundle);
})();
